import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

// Inline palette picker.
// Default state: only the current palette dot is visible, centered.
// Tap the dot → all other palettes bloom out smoothly to the left and
// right around it. Picking a colour re-collapses the row back to one
// dot in the middle. Tapping outside also collapses.
export default function ProfilePalettePicker({ testIdPrefix = 'profile-palette' }) {
  const { palette, setPalette, palettes } = useTheme();
  const [expanded, setExpanded] = useState(false);
  const rowRef = useRef(null);

  // When expanded, place the active dot in the middle and alternate the
  // remaining palettes left/right so the reveal feels symmetrical.
  const orderedPalettes = useMemo(() => {
    const activeIdx = Math.max(0, palettes.findIndex((p) => p.id === palette));
    const active = palettes[activeIdx];
    const others = palettes.filter((_, i) => i !== activeIdx);
    const left = [];
    const right = [];
    others.forEach((p, i) => {
      if (i % 2 === 0) right.push(p);
      else left.unshift(p);
    });
    return [...left, active, ...right];
  }, [palette, palettes]);

  // Collapse when tapping outside the row.
  useEffect(() => {
    if (!expanded) return undefined;
    const onDocDown = (e) => {
      if (rowRef.current && !rowRef.current.contains(e.target)) {
        setExpanded(false);
      }
    };
    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('touchstart', onDocDown, { passive: true });
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      document.removeEventListener('touchstart', onDocDown);
    };
  }, [expanded]);

  const handleDotClick = (id) => {
    if (!expanded) {
      setExpanded(true);
      return;
    }
    if (id !== palette) setPalette(id);
    setExpanded(false);
  };

  const renderDot = (p, { showCheck = true } = {}) => {
    const active = palette === p.id;
    const [primary, secondary, soft] = p.swatches;
    return (
      <motion.button
        key={p.id}
        type="button"
        layout
        data-testid={`${testIdPrefix}-${p.id}`}
        onClick={() => handleDotClick(p.id)}
        initial={{ opacity: 0, scale: 0.2 }}
        animate={{ opacity: 1, scale: active ? 1.05 : 1 }}
        exit={{ opacity: 0, scale: 0.2 }}
        transition={{ type: 'spring', stiffness: 380, damping: 28, mass: 0.6 }}
        whileTap={{ scale: 0.9 }}
        aria-label={active ? `Current palette: ${p.name}. Tap to change.` : `Apply ${p.name} palette`}
        aria-pressed={active}
        title={p.name}
        style={{
          width: 13,
          height: 13,
          padding: 0,
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${primary} 0%, ${secondary} 55%, ${soft} 100%)`,
          border: 'none',
          outline: 'none',
          boxShadow: active
            ? `0 0 0 1.5px var(--surface), 0 0 0 2.5px ${primary}, 0 2px 5px ${primary}55`
            : `0 1px 2px rgba(0,0,0,0.10), 0 0 0 1px ${primary}22`,
          cursor: 'pointer',
          position: 'relative',
          overflow: 'visible',
          flexShrink: 0,
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            background: 'radial-gradient(circle at 25% 25%, rgba(255,255,255,0.35), transparent 60%)',
          }}
        />
        {active && showCheck ? (
          <span
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#FFFFFF',
              filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.35))',
            }}
          >
            <Check size={7} strokeWidth={3.2} />
          </span>
        ) : null}
      </motion.button>
    );
  };

  const activePalette = palettes.find((p) => p.id === palette) || palettes[0];

  return (
    <motion.div
      ref={rowRef}
      layout
      data-testid={`${testIdPrefix}-picker`}
      data-expanded={expanded ? 'true' : 'false'}
      transition={{ type: 'spring', stiffness: 320, damping: 30 }}
      style={{
        width: '100%',
        marginTop: 20,
        marginBottom: 20,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 7,
        padding: '2px 4px',
      }}
    >
      <AnimatePresence initial={false} mode="popLayout">
        {expanded
          ? orderedPalettes.map((p) => renderDot(p, { showCheck: true }))
          : [renderDot(activePalette, { showCheck: false })]}
      </AnimatePresence>
    </motion.div>
  );
}
